import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import PostCard from '../components/PostCard';
import ReportModal from '../components/ReportModal';
import { PersonAvatar, PeopleEmpty, PeopleLoading } from '../components/PeopleUI';
import { IconChevronLeft, IconSettings } from '../components/talkAppleIcons';
import { langNameJa, langToRegion, langFromRegion, countryOptions } from '../data/languages';

import './profile.css';

const COUNTRY_OPTIONS = countryOptions();


function ProfileCount({ value }) {
  const elementRef = useRef(null);
  const previousValue = useRef(value);

  useEffect(() => {
    const changed = previousValue.current !== value;
    previousValue.current = value;
    if (!changed || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const animation = elementRef.current?.animate?.(
      [{ transform: 'scale(1)' }, { transform: 'scale(1.22)', offset: 0.4 }, { transform: 'scale(1)' }],
      { duration: 200, easing: 'ease-out' },
    );
    return () => animation?.cancel();
  }, [value]);

  return <strong ref={elementRef} className="pf-count" style={S.statNum}>{value}</strong>;
}


export default function Profile() {
  useLayoutEffect(() => {
    document.body.dataset.profileDesign = 'social';
    return () => { delete document.body.dataset.profileDesign; };
  }, []);

  const { userId }       = useParams();
  const { user: me, refreshMe } = useAuth();
  const navigate         = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const [posts, setPosts]     = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState({ username: '', bio: '', native_lang: 'ja', learning_lang: 'en' });
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving]   = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [followBusy, setFollowBusy] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [visitorPreview, setVisitorPreview] = useState(false);
  const avatarInputRef = useRef(null);
  const isMe = !userId || String(userId) === String(me?.id);
  const showAsVisitor = Boolean(isMe && visitorPreview);


  useEffect(() => {
    setVisitorPreview(false);
  }, [userId]);

  useEffect(() => {
    if (!isMe) setVisitorPreview(false);
  }, [isMe]);

  useEffect(() => {
    const uid = userId || me?.id;
    if (!uid) return;
    let active = true;
    setProfile(null); setPosts([]); setEditing(false); setLoadError(''); setActionError('');
    window.scrollTo(0, 0);
    axios.get(`/api/users/${uid}`)
      .then(r => { if (active) { setProfile(r.data.user); setPosts(r.data.posts || []); } })
      .catch(() => { if (active) setLoadError('プロフィールを読み込めませんでした。'); });
    return () => { active = false; };
  }, [userId, me?.id, reload]);

  const openChatWithPartner = async () => {
    if (showAsVisitor || chatBusy) return;
    setChatBusy(true); setActionError('');
    try {
      if (!profile.is_contact) {
        await axios.post('/api/contacts', { partner_id: profile.id });
        setProfile(p => ({ ...p, is_contact: true }));
      }
      navigate(`/chat/${profile.id}`);
    } catch { setActionError('チャットを開けませんでした。もう一度お試しください。'); }
    finally { setChatBusy(false); }
  };

  const toggleFollow = async () => {
    if (isMe || showAsVisitor) return;
    setFollowBusy(true);
    setActionError('');
    try {
      if (profile.is_following) {
        const { data } = await axios.delete(`/api/users/${userId}/follow`);
        setProfile(p => ({ ...p, ...data }));
      } else {
        const { data } = await axios.post(`/api/users/${userId}/follow`);
        setProfile(p => ({ ...p, ...data }));
      }
    } catch (_) {
      setActionError('フォローを変更できませんでした。もう一度お試しください。');
    } finally {
      setFollowBusy(false);
    }
  };

  const toggleBlock = async () => {
    if (isMe || showAsVisitor) return;
    setBlockBusy(true);
    setActionError('');
    try {
      if (profile.is_blocked) {
        await axios.delete(`/api/users/${userId}/block`);
        setProfile(p => ({ ...p, is_blocked: false }));
      } else {
        if (!window.confirm('このユーザーをブロックしますか？')) return;
        await axios.post(`/api/users/${userId}/block`);
        setProfile(p => ({ ...p, is_blocked: true, is_following: false }));
      }
    } catch (_) { setActionError('ブロック設定を変更できませんでした。'); } finally {
      setBlockBusy(false);
    }
  };

  const startEdit = () => {
    setForm({
      username: profile.username || '',
      bio: profile.bio || '',
      native_lang: profile.native_lang || 'ja',
      learning_lang: profile.learning_lang || 'en',
    });
    setSaveError('');
    setAvatarError('');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setSaveError('');
    setAvatarError('');
  };

  const reloadProfilePage = async () => {
    const uid = userId || me?.id;
    if (!uid) return;
    const { data: page } = await axios.get(`/api/users/${uid}`);
    setProfile({ ...page.user, is_contact: page.user.is_contact });
    setPosts(page.posts);
  };

  const uploadAvatar = async (file) => {
    setAvatarError('');
    if (!file.type.match(/^image\/(jpeg|png|gif|webp)$/i)) {
      setAvatarError('JPEG / PNG / GIF / WebP のみ利用できます');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setAvatarError('4MB 以下の画像にしてください');
      return;
    }
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      await axios.post('/api/users/me/avatar', fd);
      await reloadProfilePage();
      await refreshMe();
    } catch (e) {
      setAvatarError(e.response?.data?.error || '写真のアップロードに失敗しました');
    } finally {
      setAvatarBusy(false);
    }
  };

  const onAvatarInputChange = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) uploadAvatar(f);
  };

  const removeAvatar = async () => {
    setAvatarError('');
    if (!profile?.avatar) return;
    setAvatarBusy(true);
    try {
      await axios.delete('/api/users/me/avatar');
      await reloadProfilePage();
      await refreshMe();
    } catch (e) {
      setAvatarError(e.response?.data?.error || '削除に失敗しました');
    } finally {
      setAvatarBusy(false);
    }
  };

  const saveProfile = async () => {
    setSaveError('');
    if (!form.username.trim() || form.username.trim().length < 2) {
      setSaveError('ユーザー名は2文字以上にしてください');
      return;
    }
    setSaving(true);
    try {
      await axios.put('/api/users/me', {
        username: form.username.trim(),
        bio: form.bio,
        native_lang: form.native_lang,
        learning_lang: form.learning_lang,
      });
      const { data: page } = await axios.get(`/api/users/${userId || me?.id}`);
      setProfile({ ...page.user, is_contact: page.user.is_contact });
      setPosts(page.posts);
      await refreshMe();
      setEditing(false);
    } catch (e) {
      setSaveError(e.response?.data?.error || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return <main className="jt-screen jt-profile">{loadError ? <PeopleEmpty title={loadError} description="接続を確認して、もう一度お試しください。" onRetry={() => setReload(value => value + 1)} /> : <PeopleLoading />}</main>;

  return (
    <div className="jt-screen jt-profile" style={S.page}>
      {/* 常にDOMに存在する隠しfile input（アバタータップ用） */}
      <input
        ref={avatarInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        style={{ display: 'none' }}
        onChange={onAvatarInputChange}
      />

      <header className="pf-header" style={S.header}>
        {!isMe && <button data-control="icon" data-control-id="Profile-back" type="button" style={S.back} aria-label="戻る" onClick={() => window.history.state?.idx > 0 ? navigate(-1) : navigate('/search')}><IconChevronLeft size={22} /></button>}
        <h1 style={S.headerTitle}>{isMe && editing && !showAsVisitor ? 'プロフィール編集' : isMe ? 'マイページ' : 'プロフィール'}</h1>
        {isMe && (
          <div style={S.headerActions}>
            <button data-control="icon" data-control-id="Profile-settingsBtn" type="button" style={S.settingsBtn} onClick={() => navigate('/settings')} aria-label="設定">
              <IconSettings size={22} />
            </button>
          </div>
        )}
      </header>
      {actionError && <p className="jt-feedback" role="alert">{actionError}</p>}

      {showAsVisitor && (
        <div style={S.previewBanner} role="status">
          <p style={S.previewBannerText}>
            他のユーザーから見える表示のプレビューです。フォロー・チャットは実行されません。
          </p>
          <button data-control="secondary" data-control-id="Profile-previewBannerBtn"
            type="button"
            style={S.previewBannerBtn}
            onClick={() => setVisitorPreview(false)}
          >
            プレビューを終了
          </button>
        </div>
      )}

      {/* Profile summary and editing */}
      <div className="pf-summary" style={S.hero}>

        {isMe && editing && !showAsVisitor ? (
          /* Edit mode: full-width form */
          <div style={S.editForm}>
            <div style={S.editAvatarRow}>
              <div style={S.avatarRingSmall}>
                <div style={S.avatar}>
                  {profile.avatar
                    ? <img src={profile.avatar} alt="" style={S.avatarImg} />
                    : <span style={S.avatarTxtSm}>{profile.username[0].toUpperCase()}</span>
                  }
                </div>
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                <button data-control="secondary" data-control-id="Profile-avatarBtn"
                  type="button"
                  style={S.avatarBtn}
                  disabled={avatarBusy}
                  onClick={() => avatarInputRef.current?.click()}
                >
                  {avatarBusy ? '処理中…' : '写真を変更'}
                </button>
                {profile.avatar && (
                  <button data-control="quiet" data-control-id="Profile-avatarRemoveBtn" type="button" style={S.avatarRemoveBtn} disabled={avatarBusy} onClick={removeAvatar}>
                    写真を削除
                  </button>
                )}
              </div>
            </div>
            {avatarError ? <p style={S.avatarErr}>{avatarError}</p> : null}
            <label style={S.fieldLabel} htmlFor="profile-username">ユーザー名</label>
            <input
              id="profile-username"
              style={S.input}
              value={form.username}
              onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
              maxLength={32}
              autoComplete="username"
            />
            <label style={S.fieldLabel} htmlFor="profile-bio">自己紹介</label>
            <textarea
              id="profile-bio"
              style={S.textarea}
              value={form.bio}
              onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
              rows={3}
              placeholder="学習目標や趣味など"
            />
            <label style={S.fieldLabel} htmlFor="profile-native-lang">母国語</label>
            <select
              id="profile-native-lang"
              style={S.select}
              value={langToRegion(form.native_lang)}
              onChange={e => {
                const lang = langFromRegion(e.target.value);
                if (lang) setForm(f => ({ ...f, native_lang: lang }));
              }}
            >
              {COUNTRY_OPTIONS.map(({ regionCode, nameEn }) => (
                <option key={regionCode} value={regionCode}>{nameEn}</option>
              ))}
            </select>
            <label style={S.fieldLabel} htmlFor="profile-learning-lang">学習中の言語</label>
            <select
              id="profile-learning-lang"
              style={S.select}
              value={langToRegion(form.learning_lang)}
              onChange={e => {
                const lang = langFromRegion(e.target.value);
                if (lang) setForm(f => ({ ...f, learning_lang: lang }));
              }}
            >
              {COUNTRY_OPTIONS.map(({ regionCode, nameEn }) => (
                <option key={regionCode} value={regionCode}>{nameEn}</option>
              ))}
            </select>
            {saveError ? <p style={S.errorTxt} role="alert">{saveError}</p> : null}
            <div style={S.editActions}>
              <button data-control="secondary" data-control-id="Profile-cancelBtn" type="button" style={S.cancelBtn} onClick={cancelEdit} disabled={saving}>キャンセル</button>
              <button data-control="primary" data-control-id="Profile-saveBtn" type="button" style={S.saveBtn} onClick={saveProfile} disabled={saving}>{saving ? '保存中…' : '変更を保存'}</button>
            </div>
          </div>
        ) : (
          <>
            <div className={`pf-identity${Array.from(profile.username || '').length > 12 ? ' pf-long-name' : ''}`} style={S.heroTop}>
              <div className="pf-identity-copy" style={S.identityCopy}>
                <h2 className="pf-name" style={S.username}>{profile.username}</h2>
                {Number(profile.talk_premium) === 1 && <span className="pf-membership" style={S.premiumBadge}>プレミアム会員</span>}

              </div>
              <div className="pf-portrait" style={S.portrait}>
                {isMe && !showAsVisitor ? (
                  <button data-control="media" data-control-id="Profile-jt-profile-photo" type="button"
                    className="jt-profile-photo" aria-label="プロフィール写真を変更" disabled={avatarBusy}
                    onClick={() => avatarInputRef.current?.click()}>
                    <PersonAvatar person={profile} size={96} />
                  </button>
                ) : <PersonAvatar person={profile} size={96} />}
              </div>
            </div>
                <dl className="pf-languages" style={S.languages}>
                  <div style={S.language}><dt style={S.languageLabel}>話せる言語</dt><dd style={S.languageValue}>{langNameJa(profile.native_lang)}</dd></div>
                  <div style={S.language}><dt style={S.languageLabel}>学習中</dt><dd style={S.languageValue}>{langNameJa(profile.learning_lang)}</dd></div>
                </dl>
            {avatarError && <p style={S.avatarErr} role="alert">{avatarError}</p>}
            <div className="pf-bio" style={S.infoBlock}>
              {profile.bio ? <p style={S.bio}>{profile.bio}</p> : isMe && !showAsVisitor ? <button data-control="text" data-control-id="Profile-bioHint" type="button" style={S.bioHint} onClick={startEdit}>自己紹介を追加</button> : null}
            </div>

            <div className="pf-stats" style={S.statsRow}>
                <span style={S.stat}><ProfileCount value={profile.follower_count ?? 0} /><span style={S.statLabel}>フォロワー</span></span>
                <span style={S.stat}><ProfileCount value={profile.following_count ?? 0} /><span style={S.statLabel}>フォロー中</span></span>
              </div>

            {/* ── My profile CTAs ── */}
            {isMe && !showAsVisitor && (
              <div className="pf-actions" style={S.ownActions}>
                <button data-control="secondary" data-control-id="Profile-editProfileBtn" type="button" style={S.editProfileBtn} onClick={startEdit}>プロフィールを編集</button>
                <button type="button" style={S.editProfileBtn} onClick={() => navigate('/words')}>単語帳</button>
              </div>
            )}

            {/* ── Other user CTAs ── */}
            {(!isMe || showAsVisitor) && (
              <div className="pf-actions" style={S.actionRow}>
                <button data-control="secondary" data-control-id="Profile-followBtn" aria-pressed={!!profile.is_following}
                  type="button"
                  style={{
                    ...S.followBtn,
                    ...(profile.is_following ? S.followBtnOn : {}),
                    ...(showAsVisitor ? S.ctaDisabled : {}),
                  }}
                  onClick={toggleFollow}
                  disabled={followBusy || showAsVisitor}
                >
                  {followBusy ? '…' : profile.is_following ? 'フォロー中' : 'フォローする'}
                </button>
                <button data-control="primary" data-control-id="Profile-chatBtn"
                  type="button"
                  style={{ ...S.chatBtn, ...(showAsVisitor ? S.ctaDisabled : {}) }}
                  onClick={openChatWithPartner}
                  disabled={showAsVisitor || chatBusy || profile.is_blocked}
                >
                  {chatBusy ? '開いています…' : 'メッセージを書く'}
                </button>
              </div>
            )}


            {/* ── 安全のための操作（Guideline 1.2）。主要CTAとは行を分ける ── */}
            {(!isMe || showAsVisitor) && (
              <div style={S.safetyRow}>
                <button data-control="quiet" data-control-id="Profile-blockBtn"
                  type="button"
                  style={{ ...S.blockBtn, ...(profile.is_blocked ? S.blockBtnOn : {}) }}
                  onClick={toggleBlock}
                  disabled={blockBusy || showAsVisitor}
                >
                  {blockBusy ? '…' : profile.is_blocked ? 'ブロック解除' : 'ブロック'}
                </button>
                <button data-control="quiet" data-control-id="Profile-reportBtn"
                  type="button"
                  style={{ ...S.reportBtn, ...(showAsVisitor ? S.ctaDisabled : {}) }}
                  onClick={() => setReportOpen(true)}
                  disabled={showAsVisitor}
                >
                  通報
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Posts section ── */}
      <div className="pf-posts-heading" style={S.postsHeader}>
        <h2 style={S.postsTitle}>投稿</h2>
        <span style={S.postsCount}>{posts.length}</span>
      </div>
      {posts.length === 0 && <PeopleEmpty title={isMe ? 'まだ投稿はありません' : '表示できる投稿はありません'} description={isMe ? '投稿すると、ここに表示されます。' : '新しい投稿はここに表示されます。'} to={isMe ? '/?compose=1' : undefined} action="投稿する" />}
      {posts.map(post => (
        <PostCard
          key={post.id}
          post={post}
          onHidden={id => setPosts(ps => ps.filter(p => p.id !== id))}
        />
      ))}

      {reportOpen && profile && (
        <ReportModal
          targetType="user"
          targetId={profile.id}
          targetName={profile.username}
          onClose={() => setReportOpen(false)}
          onReported={() => { setPosts([]); }}
        />
      )}

    </div>
  );
}

const caption = { fontSize: 12, lineHeight: '20px', letterSpacing: '.08em' };
const control = { minWidth: 48, minHeight: 48, borderRadius: 9999, padding: '8px 16px', fontSize: 16, lineHeight: '24px', fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' };
const field = { width: '100%', minHeight: 48, padding: '8px 16px', borderRadius: 4, border: '1px solid var(--border)', fontSize: 16, lineHeight: '24px', fontFamily: 'inherit', fontWeight: 400, background: 'var(--surface)', color: 'var(--text)' };
const S = {
  page: { background: 'var(--surface)', color: 'var(--text)', paddingBottom: 48 },
  header: { display: 'flex', alignItems: 'center', gap: 8, minHeight: 64, padding: '8px 24px', borderBottom: '1px solid var(--border-light)' },
  back: { ...control, background: 'transparent', border: 0, padding: 0, width: 48, display: 'grid', placeItems: 'center', flexShrink: 0 },
  headerTitle: { margin: 0, flex: 1, fontSize: 16, lineHeight: '24px', fontWeight: 500 },
  headerActions: { display: 'flex', alignItems: 'center' },
  settingsBtn: { ...control, background: 'transparent', display: 'grid', placeItems: 'center', width: 48, padding: 0, border: 0 },
  hero: { margin: 0, padding: '24px 24px 0' },
  heroTop: { display: 'grid', gridTemplateColumns: '96px minmax(0, 1fr)', gap: 16, alignItems: 'center' },
  identityCopy: { minWidth: 0, gridColumn: 2, gridRow: 1 },
  portrait: { width: 96, height: 96, gridColumn: 1, gridRow: 1 },
  username: { margin: 0, fontSize: 36, lineHeight: '44px', fontWeight: 600, overflowWrap: 'anywhere', letterSpacing: '-.025em' },
  premiumBadge: { ...caption, display: 'block', marginTop: 8, color: 'var(--text-secondary)' },
  languages: { margin: '24px 0 0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  language: { display: 'flex', flexDirection: 'column', gap: 4 },
  languageLabel: { ...caption, color: 'var(--text-secondary)' },
  languageValue: { margin: 0, fontSize: 16, lineHeight: '24px', fontWeight: 400, overflowWrap: 'anywhere' },
  infoBlock: { marginTop: 24 },
  bio: { fontSize: 16, lineHeight: '28px', margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxWidth: '64ch' },
  bioHint: { ...control, color: 'var(--primary-dark)', padding: 0, background: 'transparent', border: 0, textAlign: 'left' },
  statsRow: { display: 'flex', flexWrap: 'wrap', gap: 24, padding: 0, marginTop: 24 },
  stat: { ...caption, display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 },
  statNum: { fontSize: 16, lineHeight: '24px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  statLabel: { ...caption, color: 'var(--text-secondary)' },
  actionRow: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 24 },
  ownActions: { display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: 8, marginTop: 24 },
  editProfileBtn: { ...control, background: 'var(--surface)', flex: 1 },
  followBtn: { ...control, flex: 1 },
  followBtnOn: { color: 'var(--text-secondary)', borderColor: 'var(--border)', background: 'var(--bg)' },
  chatBtn: { ...control, borderColor: 'var(--primary)', background: 'var(--primary)', color: 'var(--surface)', flex: 1 },
  safetyRow: { display: 'flex', gap: 16, marginTop: 8 },
  blockBtn: { ...control, ...caption, padding: 0, border: 0, background: 'transparent', color: 'var(--text-secondary)' },
  blockBtnOn: { color: 'var(--text)' },
  reportBtn: { ...control, ...caption, padding: 0, border: 0, background: 'transparent', color: 'var(--text-secondary)' },
  ctaDisabled: { cursor: 'default' },
  postsHeader: { display: 'flex', gap: 8, alignItems: 'baseline', margin: '32px 0 0', padding: '16px 24px', borderTop: '1px solid var(--border)' },
  postsTitle: { margin: 0, fontSize: 24, lineHeight: '32px', fontWeight: 600, letterSpacing: '-.025em' },
  postsCount: { ...caption, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' },
  previewBanner: { margin: '16px 24px 0', padding: 16, background: 'var(--bg)', border: '.5px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' },
  previewBannerText: { fontSize: 16, lineHeight: '28px', margin: 0, color: 'var(--text-secondary)' },
  previewBannerBtn: { ...control, ...caption },
  editForm: { display: 'flex', flexDirection: 'column', gap: 4, paddingBottom: 24 },
  editAvatarRow: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 },
  avatarRingSmall: { width: 96, height: 96, flexShrink: 0 },
  avatar: { width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', background: 'var(--bg)', display: 'grid', placeItems: 'center' },
  avatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
  avatarTxtSm: { fontSize: 36, lineHeight: '44px', letterSpacing: '-.025em', color: 'var(--text-secondary)' },
  avatarBtn: { ...control, borderRadius: 4, minHeight: 44, padding: '8px 14px', fontSize: 14, background: 'var(--surface)' },
  avatarRemoveBtn: { ...control, borderRadius: 4, minHeight: 44, padding: '8px 14px', fontSize: 14, border: 0, background: 'transparent', color: 'var(--text-secondary)' },
  avatarErr: { ...caption, color: 'var(--text)', margin: '8px 0', borderLeft: '1px solid var(--text)', paddingLeft: 8 },
  fieldLabel: { ...caption, marginTop: 16, fontWeight: 400 },
  input: field,
  textarea: { ...field, resize: 'vertical', minHeight: 128, lineHeight: '28px' },
  select: { ...field, cursor: 'pointer' },
  errorTxt: { ...caption, color: 'var(--text)', borderLeft: '1px solid var(--text)', paddingLeft: 8 },
  editActions: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 24 },
  cancelBtn: { ...control },
  saveBtn: { ...control, background: 'var(--primary)', color: 'var(--surface)', borderColor: 'var(--primary)' },
};
